import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Switch } from '@appcues/sonar';
import {
  FieldSet,
  Label,
  RadioButton,
  RadioButtonGroup,
  Select,
} from 'ext/components/ui';
import { useAnalytics } from '@appcues/analytics/hooks/use-analytics';
import { EditorShape } from 'entities/step-groups/shape';
import { dropShadowTemplate } from 'entities/step-groups/templates/drop-shadow';
import { pagingDotsStyleTemplate } from 'entities/step-groups/templates/paging-dots';
import { slideoutTemplate } from 'entities/step-groups/templates/modal';
import { MODAL, SKIPPABLE_OPTIONS } from 'lib/trait/trait';
import {
  PRESENTATIONS,
  STANDARD,
  SLIDEOUT,
  FULL_SCREEN,
} from 'lib/presentation/presentation';
import { Controls, useAccordionClick } from 'components/SideBarSettings/Shared';
import { SurveyCarouselWarning } from 'components/SideBarSettings/Trait/SurveyCarouselWarning';

const PRESENTATION_OPTIONS = PRESENTATIONS[MODAL];

const ALIGNMENT = {
  top: 'top',
  center: 'center',
  bottom: 'bottom',
};

const { style: slideoutStyleTemplate, ...slideoutDetailsTemplate } =
  slideoutTemplate;

export default function Details({
  editor,
  isSingleStep,
  hasSurveyBlock,
  onChange,
}) {
  const { track } = useAnalytics();
  const trackAccordion = useAccordionClick();
  const { carousel, presentation, skippable, pagingDots, style } = editor ?? {};
  const { ignoreBackdropTap = false } = skippable ?? {};
  const verticalAlignment = style?.verticalAlignment || ALIGNMENT.center;

  const handleTrack = useCallback(
    name => {
      track('Mobile Builder interaction', {
        name,
        component: 'ModalSettings - Details',
      });
    },
    [track]
  );

  const options = useMemo(() => {
    const entries = Object.entries(PRESENTATION_OPTIONS);
    return entries.map(([value, label]) => ({ value, label }));
  }, []);

  const handlePresentationUpdate = useCallback(
    ({ value }) => {
      handleTrack(`Updated Presentation ${PRESENTATIONS[value]}`);

      const {
        borderColor,
        borderWidth,
        verticalAlignment: previousVerticalAlignment,
        horizontalAlignment: previousHorizontalAlignment,
        width,
        marginTop,
        marginBottom,
        marginLeading,
        marginTrailing,
        ...styleRest
      } = style || {};

      const baseDetailsTemplate = {
        slideout: false,
        ...(value === STANDARD && { transition: 'fade' }),
      };

      onChange({
        presentation: value,
        ...(value === SLIDEOUT ? slideoutDetailsTemplate : baseDetailsTemplate),
        style: {
          ...styleRest,
          // Shadow and border values can persist between standard and slideout presentations
          ...(![STANDARD, SLIDEOUT].includes(value) && {
            shadow: dropShadowTemplate,
          }),
          ...([STANDARD, SLIDEOUT].includes(value) && {
            ...(borderColor && { borderColor }),
            ...(borderWidth && { borderWidth }),
          }),
          ...(value === SLIDEOUT && slideoutStyleTemplate),
        },
      });
    },
    [handleTrack, style, onChange]
  );

  const handleSkippableUpdate = useCallback(
    value => {
      const { DEFAULT, HIDDEN } = SKIPPABLE_OPTIONS;
      const buttonAppearance = value ? DEFAULT : HIDDEN;
      handleTrack(`${value ? 'Added' : 'Removed'} Modal close`);
      onChange({
        skippable: {
          ...skippable,
          buttonAppearance,
        },
      });
    },
    [onChange, skippable, handleTrack]
  );

  const handleTapOutUpdate = value => {
    handleTrack(`${value ? 'Added' : 'Removed'} Tap out to dismiss`);
    onChange({ skippable: { ...skippable, ignoreBackdropTap: !value } });
  };

  // Manage style object when adding or remove the entire pagingDots trait otherwise
  const handlePagingDotsUpdate = useCallback(() => {
    handleTrack(`${!pagingDots ? 'Added' : 'Removed'} Step indicator`);
    if (pagingDots) {
      onChange(null, 'pagingDots');
    } else {
      onChange({
        pagingDots: pagingDotsStyleTemplate,
      });
    }
  }, [onChange, pagingDots, handleTrack]);

  const handleCarouselUpdate = useCallback(
    value => {
      handleTrack(`${value ? 'Added' : 'Removed'} Swipe for next step`);
      onChange({ carousel: value });
    },
    [onChange, handleTrack]
  );

  const handleAlignmentUpdate = useCallback(
    value => {
      onChange({
        style: {
          ...style,
          verticalAlignment: value,
        },
      });
    },
    [style, onChange]
  );

  return (
    <Accordion.Item value="details">
      <Accordion.Header>
        <Accordion.Trigger onClick={() => trackAccordion('Modal', 'Details')}>
          Details
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content>
        <Controls>
          <FieldSet>
            <Label>Type</Label>
            <Select
              onChange={handlePresentationUpdate}
              options={options}
              portal
              value={options.find(({ value }) => presentation === value)}
            />
          </FieldSet>

          {presentation === STANDARD && (
            <FieldSet>
              <Label>Position</Label>
              <RadioButtonGroup>
                <RadioButton
                  onClick={() => handleAlignmentUpdate(ALIGNMENT.top)}
                  selected={verticalAlignment === ALIGNMENT.top}
                >
                  Top
                </RadioButton>
                <RadioButton
                  onClick={() => handleAlignmentUpdate(ALIGNMENT.center)}
                  selected={verticalAlignment === ALIGNMENT.center}
                >
                  Middle
                </RadioButton>
                <RadioButton
                  onClick={() => handleAlignmentUpdate(ALIGNMENT.bottom)}
                  selected={verticalAlignment === ALIGNMENT.bottom}
                >
                  Bottom
                </RadioButton>
              </RadioButtonGroup>
            </FieldSet>
          )}

          <FieldSet>
            <Switch
              id="close-x"
              checked={skippable?.buttonAppearance !== SKIPPABLE_OPTIONS.HIDDEN}
              onCheckedChange={handleSkippableUpdate}
              fullWidth
            >
              <Switch.Label htmlFor="close-x">Close X</Switch.Label>
            </Switch>
          </FieldSet>

          {presentation !== FULL_SCREEN && (
            <FieldSet>
              <Switch
                id="tap-out"
                checked={!ignoreBackdropTap}
                onCheckedChange={handleTapOutUpdate}
                fullWidth
              >
                <Switch.Label htmlFor="tap-out">
                  Tap out to dismiss
                </Switch.Label>
              </Switch>
            </FieldSet>
          )}

          {!isSingleStep && (
            <>
              <FieldSet>
                <Switch
                  id="step-indicator"
                  checked={!!pagingDots}
                  onCheckedChange={handlePagingDotsUpdate}
                  fullWidth
                >
                  <Switch.Label htmlFor="step-indicator">
                    Step indicator
                  </Switch.Label>
                </Switch>
              </FieldSet>

              <FieldSet>
                <Switch
                  id="swipe"
                  checked={carousel}
                  onCheckedChange={handleCarouselUpdate}
                  fullWidth
                >
                  <Switch.Label htmlFor="swipe">
                    Swipe for next step
                  </Switch.Label>
                </Switch>
              </FieldSet>
              {hasSurveyBlock && carousel && <SurveyCarouselWarning />}
            </>
          )}
        </Controls>
      </Accordion.Content>
    </Accordion.Item>
  );
}

Details.propTypes = {
  editor: EditorShape,
  isSingleStep: PropTypes.bool,
  hasSurveyBlock: PropTypes.bool,
  onChange: PropTypes.func,
};
